import { state, style, trigger } from '@angular/animations';
import {
  Component,
  Directive,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { appRoutes } from '@tekman/onmat/constants';
import {
  Activity,
  ActivityTypologyName,
  CheckboxItemFlatNode,
  CheckboxItemNode,
  Exercise,
  FeatureName,
  Materials,
  PlatformResource,
  Resources,
  SelfAssessment,
  SideNavFeature,
  Tags,
  TeachingInfo,
  UpdatedActivityFeature,
} from '@tekman/onmat/models';
import { EnvironmentService, UserService } from '@tekman/onmat/service/generic';
import { FeaturitWrapperService } from '@tekman/onmat/service/onmat';
import { ActivitiesFacade as teacherActivitiesFacade } from '@tekman/onmat/store/teacher/activities';
import { FeaturitEventActivity, ModalOptions } from '@tekman/shared/models';
import { ModalFactoryService, ModalService } from '@tekman/shared/service';
import { Observable, Subscription } from 'rxjs';

@Directive({ selector: 'panel' })
export class PanelDirective {
  @Input() id!: string;
}

@Component({
  selector: 'side-nav-menu',
  templateUrl: './side-nav-menu.component.html',
  styleUrls: ['./side-nav-menu.component.scss'],
  animations: [
    trigger('enabledStateChange', [
      state(
        'enabled',
        style({
          flex: 1,
        })
      ),
      state(
        'disabled',
        style({
          flex: 0,
        })
      ),
    ]),
  ],
})
export class SideNavMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input() activity: Activity;
  @Input() exercises: Exercise[];
  @Input() isTry: boolean;
  @Input() selectedGroupId: string;
  @Output() opened = new EventEmitter<boolean>();
  setCheck: any;
  isTeacher: boolean;
  showInfoPanel = false;
  showMaterialPanel = false;
  showConfigPanel = false;
  showResourcesPanel = false;
  showTypeformPanel = false;
  videoResourceURL: SafeResourceUrl;
  videoResourceTitle = '';
  showVideoModal = false;
  resizing = false;
  featureName: typeof FeatureName = FeatureName;

  features: SideNavFeature[] = [];
  enabledFeature: SideNavFeature;
  avoidRefresh: boolean;

  mouse: MouseEvent;

  visible = false;

  get isVisible(): boolean {
    return this.visible;
  }

  stateOfElement = 'disabled';
  subscription: Subscription;
  tags$: Observable<Tags[]>;
  features$: Observable<SideNavFeature[]>;
  private auxFeature: CheckboxItemFlatNode[] = [];

  platformResources: PlatformResource[] = [
    { title: 'f.side-nav.resource.mahara', link: 'https://mahara.org/', image: 'mahara' },
    { title: 'f.side-nav.resource.geogebra', link: 'https://www.geogebra.org/calculator', image: 'geogebra' },
    { title: 'f.side-nav.resource.mindmup', link: 'https://www.mindmup.com/', image: 'mindmup' },
  ];
  stepResources$: Observable<Resources>;
  resource: number;
  enableResourcesPanel = false;

  get isVisibleResources(): boolean {
    return this.enableResourcesPanel;
  }

  teachingInfo$: Observable<TeachingInfo>;
  teachingInfo: number;
  enableTeachingPanel = false;
  selfAssessmentResources$: Observable<SelfAssessment[]>;

  materials$: Observable<Materials>;
  public appRoutes = appRoutes;
  enabledEvaluationFeature: boolean;
  activityTypologyName: typeof ActivityTypologyName = ActivityTypologyName;

  modal: ModalService;
  @ViewChild('MaterialLinkTemplate') materialLinkTemplate: TemplateRef<any>;
  urlLinkFormControl = new UntypedFormControl('', [
    Validators.required,
    // eslint-disable-next-line max-len
    Validators.pattern(
      new RegExp(
        // eslint-disable-next-line max-len
        /^(?:(?:(?:https?):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/
      )
    ),
  ]);
  textLinkFormControl = new UntypedFormControl('', [Validators.required]);
  isLomloeEnv: boolean;

  constructor(
    public readonly teacherFacade: teacherActivitiesFacade,
    private readonly translate: TranslateService,
    protected sanitizer: DomSanitizer,
    private readonly modalFactoryService: ModalFactoryService,
    private readonly userService: UserService,
    private readonly featuritService: FeaturitWrapperService,
    private readonly env: EnvironmentService
  ) {}

  ngOnInit() {
    this.isLomloeEnv = this.env.settings?.isLomloeEnv;
    this.isTeacher = this.userService.isTeacher();
    if (this.isTeacher) {
      this.teachingInfo$ = this.teacherFacade.teachingInfo$;
    }
    this.getFeatures();
    this.materials$ = this.teacherFacade.materials$;
    this.materials$ = this.teacherFacade.materials$;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['clickedOption'] && changes['clickedOption'].currentValue !== undefined) {
      this.showInfoPanel = this.showResourcesPanel = this.showMaterialPanel = this.showConfigPanel = this.showTypeformPanel = false;
      this.crossClose();
    }
  }

  /** Method to get features by id activity and group */
  private getFeatures(): void {
    this.tags$ = this.teacherFacade.tags$;
    this.features$ = this.teacherFacade.features$;
    this.subscription = this.tags$.subscribe(tags => {
      if (tags) {
        this.crossClose();
      }
    });
    this.subscription.add(
      this.features$.subscribe(features => {
        this.features = features;
      })
    );
  }

  toggle($event) {
    let clickedElement = $event.target || $event.srcElement;
    if (clickedElement.nodeName === 'BUTTON') {
      this.expanseLateral(clickedElement);
    } else if (
      (clickedElement.nodeName === 'svg' && clickedElement.parentElement.nodeName === 'SVG-ICON') ||
      (clickedElement.nodeName === 'path' && clickedElement.parentElement.nodeName === 'svg')
    ) {
      if (clickedElement.parentElement !== null) {
        if (clickedElement.parentElement.parentElement.nodeName === 'SVG-ICON') {
          clickedElement = clickedElement.parentElement.parentElement.parentElement;
        } else {
          clickedElement = clickedElement.parentElement.parentElement;
        }
      }
      this.expanseLateral(clickedElement);
    }
  }

  get isSolutionaryAvailable(): boolean {
    return (
      this.activity?.isSolutionaryAvailable &&
      !!this.exercises.filter(exercise => {
        if (exercise.hasSolution) return exercise;
      }).length
    );
  }

  track(eventKey, uiLabel, uiPath) {
    const trackingProps: FeaturitEventActivity<string> = new FeaturitEventActivity({
      ui_path: 'activities',
      activity_id: this.activity.id,
      activity_title: this.activity.title,
      step_id: this.activity.step.id,
      step_title: this.activity.step.title,
    });
    this.featuritService.track(eventKey, trackingProps);
  }

  expanseLateral(clickedElement) {
    const menu = clickedElement.parentElement;
    const preMenu = menu.parentElement;
    const svg = preMenu.querySelector('.svg');
    const menuBorder = menu.querySelector('.menu__border');
    const activeItem = menu.querySelector('.active');

    if (clickedElement.classList.contains('active')) {
      clickedElement.classList.remove('active');
      this.opened.emit(false);
      menuBorder.classList.add('menu__border__inactive');
      svg.classList.add('menu__border__inactive');
      this.stateOfElement = 'disabled';
    } else {
      if (activeItem) {
        activeItem.classList.remove('active');
        this.opened.emit(false);
        menuBorder.classList.add('menu__border__inactive');
        svg.classList.add('menu__border__inactive');
      }
      clickedElement.classList.add('active');
      this.opened.emit(true);
      menuBorder.classList.remove('menu__border__inactive');
      svg.classList.remove('menu__border__inactive');
      if (this.stateOfElement === 'disabled') this.stateOfElement = 'enabled';
    }
    this.offsetMenuBorder(clickedElement, menuBorder);
    // TODO Habria que cambiar la forma de tratar esto utilizando correctamente el panel (ViewChild)
    switch (clickedElement.attributes.id.nodeValue) {
      case 'btnInfo':
        this.showInfoPanel = !this.showInfoPanel;
        this.showConfigPanel = false;
        this.showResourcesPanel = false;
        this.showTypeformPanel = false;
        this.showMaterialPanel = false;
        if (this.showInfoPanel) {
          this.track('info_show', '', 'activities');
        }
        break;
      case 'btnMaterial':
        this.showInfoPanel = false;
        this.showMaterialPanel = !this.showMaterialPanel;
        this.showConfigPanel = false;
        this.showResourcesPanel = false;
        this.showTypeformPanel = false;
        break;
      case 'btnConfig':
        this.showInfoPanel = false;
        this.showConfigPanel = !this.showConfigPanel;
        this.showResourcesPanel = false;
        this.showTypeformPanel = false;
        this.showMaterialPanel = false;

        break;
      case 'btnTypeform':
        this.showInfoPanel = false;
        this.showMaterialPanel = false;
        this.showConfigPanel = false;
        this.showResourcesPanel = false;
        this.showTypeformPanel = !this.showTypeformPanel;
        break;
      case 'btnResources':
        this.showResourcesPanel = !this.showResourcesPanel;
        this.showInfoPanel = false;
        this.showMaterialPanel = false;
        this.showConfigPanel = false;
        this.showTypeformPanel = false;
        break;
      default:
        this.showInfoPanel = false;
        this.showMaterialPanel = false;
        this.showConfigPanel = false;
        this.showResourcesPanel = false;
        this.showTypeformPanel = false;
        break;
    }
  }

  collapse($event) {
    const clickedOpt = $event.target || $event.srcElement;

    if (clickedOpt.nodeName === 'INPUT' && clickedOpt.type === 'radio') {
      if (this.setCheck !== clickedOpt) {
        this.setCheck = clickedOpt;
      } else {
        clickedOpt.checked = false;
        this.setCheck = undefined;
      }
    }
  }

  offsetMenuBorder(element, menuBorder) {
    const menu = element.parentElement;
    const offsetActiveItem = element.getBoundingClientRect();
    const left = Math.floor(offsetActiveItem.top - menu.offsetTop - (menuBorder.offsetHeight - offsetActiveItem.height) / 2 + 53) + 'px';
    menuBorder.style.transform = `translate3d(0, ${left} , 0)`;
  }

  crossClose() {
    const body = document.body;
    const menu = body.querySelector('.sidenav-options');
    const menuBorder = menu.querySelector('.menu__border');
    const preMenu = menu.parentElement;
    const svg = preMenu.querySelector('.svg');
    const activeItem = menu.querySelector('.active');

    if (activeItem) {
      activeItem.classList.remove('active');
      menuBorder.classList.add('menu__border__inactive');
      this.opened.emit(false);
      svg.classList.add('menu__border__inactive');
      this.stateOfElement = 'disabled';
    }
    this.showInfoPanel = false;
    this.showConfigPanel = false;
    this.showResourcesPanel = false;
    this.showTypeformPanel = false;
    this.showMaterialPanel = false;
  }

  tooltipFollowMouse(event: MouseEvent) {
    this.mouse = event;
  }

  amountCustomizeStudents(feature: SideNavFeature): boolean {
    if (feature.customize.length > 0) {
      const amountStudents = feature.customize[0].children.length;
      const amountChecked = feature.customize[0].children.filter(student => student.checked).length;
      return amountStudents === amountChecked || amountChecked === 0;
    } else {
      return true;
    }
  }

  customizeFeature(feature: SideNavFeature, checked: boolean): void {
    if (feature.customize.length > 0) {
      const checkboxTree: CheckboxItemNode[] = [
        {
          name: this.translate.instant('f.side-nav.selectAll'),
          children: [],
          checked: checked,
        },
      ];
      checkboxTree[0].children = feature.customize[0].children.map(itemCheckbox => {
        itemCheckbox = { ...itemCheckbox, checked: checked };
        return itemCheckbox;
      });
      feature = { ...feature, customize: checkboxTree };
    }
    this.features = this.features.map(item => {
      if (item.name === feature.name) {
        feature = item = { ...item, active: checked, customize: feature.customize };
      }
      return item;
    });
    const updateFeature: UpdatedActivityFeature = {
      name: feature.name,
      value: feature.active,
    };
    this.mixpanelTracking(this.featuritService.eventKeys.activityConfigure, updateFeature);
    this.teacherFacade.saveFeature(this.activity, this.selectedGroupId, updateFeature, this.features);
  }

  mixpanelTracking(event: string, feature: SideNavFeature) {
    const featuresActive = this.features
      .filter(item => {
        return item.active;
      })
      .map(filteredItems => filteredItems.name);

    const trackingProps: FeaturitEventActivity<string> = new FeaturitEventActivity({
      activity: this.activity,
      activity_feature: feature.name,
      ui_path: 'activities',
    });
    this.featuritService.track(event, trackingProps);
  }

  setVisibleFeature(feature: SideNavFeature, isVisible: boolean): void {
    if (feature.customize.length === 0) {
      return;
    }
    if (isVisible) {
      this.mixpanelTracking(this.featuritService.eventKeys.activityCustomize, feature);
    }
    this.subscription.add(
      this.features$.subscribe(features => {
        this.auxFeature = [];
        this.visible = isVisible;
        if (features) {
          this.features = features.map(item => {
            if (item.name === feature.name) {
              item = { ...item, visible: isVisible };
              if (isVisible) {
                this.enabledFeature = Object.assign({}, item);
              } else {
                this.enabledFeature = Object.assign({});
              }
              if (item.customize.length > 0 && item.customize[0]) {
                item.customize[0].children.forEach(student => {
                  if (student.checked) {
                    this.auxFeature.push(student);
                  }
                });
              }
            }
            return item;
          });
        }
      })
    );
  }

  checklistSelected(feature: SideNavFeature, selectedCheckbox: CheckboxItemFlatNode[]): void {
    this.mixpanelTracking(this.featuritService.eventKeys.activityCustomize, feature);
    this.features = this.features.map(item => {
      if (item.name === feature.name) {
        feature = item = {
          ...item,
          active: selectedCheckbox.length > 0,
          amountCustom: selectedCheckbox.length,
        };
      }
      return item;
    });
    this.saveFeature(feature, selectedCheckbox);
  }

  private saveFeature(feature: SideNavFeature, selectedCheckbox: CheckboxItemFlatNode[]): void {
    if (
      (feature.customize.length > 0 && selectedCheckbox.length === feature.customize[0].children.length) ||
      selectedCheckbox.length === 0
    ) {
      const checkboxTree: CheckboxItemNode[] = [
        {
          name: this.translate.instant('f.side-nav.selectAll'),
          children: [],
        },
      ];
      selectedCheckbox = selectedCheckbox.map(element => {
        element.checked = selectedCheckbox.length !== 0;
        return element;
      });
      this.auxFeature = selectedCheckbox;
      checkboxTree[0].children = feature.customize[0].children.map(itemCheckbox => {
        itemCheckbox = { ...itemCheckbox, checked: selectedCheckbox.length !== 0 };
        return itemCheckbox;
      });
      feature = { ...feature, customize: checkboxTree };
      this.modifyFeature(feature.customize[0].children, feature);
      this.customizeFeature(feature, selectedCheckbox.length !== 0);
      return;
    }
    if (selectedCheckbox.length < this.auxFeature.length) {
      this.removeStudent(feature, selectedCheckbox);
    } else {
      this.addStudent(feature, selectedCheckbox);
    }
  }

  private removeStudent(feature: SideNavFeature, selectedCheckbox: CheckboxItemFlatNode[]) {
    let updateFeature: UpdatedActivityFeature;
    let allStudents: CheckboxItemNode[] = [];
    this.auxFeature.forEach(element => {
      const foundStudent = selectedCheckbox.filter(student => student.id === element.id);
      if (foundStudent.length === 0) {
        element = { ...element, checked: false };
        updateFeature = {
          name: feature.name,
          value: element.checked,
        };
        allStudents = feature.customize[0].children.map(itemCheckbox => {
          if (itemCheckbox.id === element.id) {
            itemCheckbox = { ...itemCheckbox, checked: false };
          }
          return itemCheckbox;
        });
        this.auxFeature = this.auxFeature.filter(auxStudent => auxStudent.id !== element.id);
        this.modifyFeature(allStudents, feature);
        this.teacherFacade.saveFeature(this.activity, this.selectedGroupId, updateFeature, this.features, element.id);
      }
    });
  }

  private addStudent(feature: SideNavFeature, selectedCheckbox: CheckboxItemFlatNode[]) {
    let updateFeature: UpdatedActivityFeature;
    let allStudents: CheckboxItemNode[] = [];
    selectedCheckbox.forEach(element => {
      const foundStudent = this.auxFeature.filter(student => student.id === element.id);
      if (foundStudent.length === 0) {
        element = { ...element, checked: true };
        updateFeature = {
          name: feature.name,
          value: element.checked,
        };
        allStudents = feature.customize[0].children.map(itemCheckbox => {
          if (itemCheckbox.id === element.id) {
            itemCheckbox = { ...itemCheckbox, checked: true };
          }
          return itemCheckbox;
        });
        this.auxFeature.push(element);
        this.modifyFeature(allStudents, feature);
        this.teacherFacade.saveFeature(this.activity, this.selectedGroupId, updateFeature, this.features, element.id);
      }
    });
  }

  private modifyFeature(allStudents: CheckboxItemNode[], feature: SideNavFeature): void {
    const amountStudents = allStudents.length;
    const amountChecked = allStudents.filter(student => student.checked).length;
    const checkboxTree: CheckboxItemNode[] = [
      {
        name: this.translate.instant('f.side-nav.selectAll'),
        children: [],
        checked: amountStudents === amountChecked,
      },
    ];
    allStudents.forEach(student => {
      checkboxTree[0].children.push(student);
    });
    feature = { ...feature, customize: checkboxTree, amountCustom: amountChecked };
    this.features = this.features.map(item => {
      if (item.name === feature.name) {
        item = feature;
      }
      return item;
    });
    this.avoidRefresh = true;
  }

  selectedResource(resource: number): void {
    this.resource = resource;
    this.showResourcePanel();
  }

  showResourcePanel(): void {
    this.enableResourcesPanel = !this.enableResourcesPanel;
  }

  openLink(link: string): void {
    window.open(link, '_blank');
  }

  selectedTeachingInfo(teachingInfo: number): void {
    let eventKey: string;
    let uiLabel: string;
    let uiPath: string;
    switch (teachingInfo) {
      case 0:
        eventKey = 'info_description_show';
        uiLabel = 'Descripción';
        uiPath = 'activities';
        break;
      case 1:
        eventKey = 'info_contents_show';
        uiLabel = 'Contenidos';
        uiPath = 'activities';
        break;
      case 2:
        eventKey = 'info_competences_show';
        uiLabel = 'Competencias';
        uiPath = 'activities';
        break;
      case 3:
        eventKey = 'info_multiples_intelligences';
        uiLabel = 'Inteligencias múltiples';
        uiPath = 'activities';
        break;
      case 4:
        eventKey = 'info_related_steps_show';
        uiLabel = 'Steps relacionados';
        uiPath = 'activities';
        break;
      default:
        eventKey = 'info_show_undefined';
        uiLabel = 'label undefined';
        uiPath = 'activities';
        break;
    }
    this.track(eventKey, uiLabel, uiPath);
    this.teachingInfo = teachingInfo;
    this.showTeachingPanel();
  }

  showTeachingPanel(): void {
    this.enableTeachingPanel = !this.enableTeachingPanel;
  }

  safeURL(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  showVideo(url: string, title: string) {
    this.videoResourceURL = this.safeURL(url);
    this.videoResourceTitle = title;
    this.showVideoModal = true;
  }

  hideVideo() {
    this.showVideoModal = false;
  }

  isResizing() {
    this.resizing = true;
  }

  openDialogMaterialLink(): void {
    const modalData = {
      template: this.materialLinkTemplate,
    };
    const options: ModalOptions = { disableClose: false };
    this.modal = this.modalFactoryService.open(modalData, options);
  }

  onCloseDialogClick(): void {
    this.textLinkFormControl.reset();
    this.urlLinkFormControl.reset();
    this.modalFactoryService.close();
  }

  onAcceptDialogClick(): void {
    if (this.urlLinkFormControl.status !== 'INVALID') {
      this.onCloseDialogClick();
    }
  }

  @HostListener('document:mouseup', ['$event']) onDocumentClick(event: any) {
    this.resizing = false;
  }

  ngOnDestroy() {
    this.subscription && this.subscription.unsubscribe();
  }
}
